import { useContext, useEffect, useMemo, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'

import objectPath from 'object-path'
import { Form, Col, Row, InputGroup } from 'react-bootstrap'
import Label from './Label'
import InputPrepend from './InputPrepend'
import InputAppend from './InputAppend'
import NumberFormat from 'react-number-format'
import { createUischemaStyle, createTextWarningStyle } from './Style'
import { Tooltip } from '@mui/material'
import { WARNING_COLOR } from '../config/Color'
import { onCheckCondition } from '../action/condition/Action'
import { truncateDecimal } from '../action/Data'
import { handleEnterAction } from '../action/Action'
import {
  cancelDebounceCallback,
  debounceCallback,
} from 'utility/debounceCallback.util'
import { getNumberInputValue } from './Number.util'

const setObjectPathValue = (data, key, payload) => {
  let path = String(key || '')
  let parentPath = path.split('.').slice(0, -1).join('.')
  let parentValue = parentPath ? objectPath.get(data, parentPath) : data
  if (
    parentPath &&
    (parentValue === undefined ||
      parentValue === null ||
      typeof parentValue !== 'object')
  ) {
    objectPath.set(data, parentPath, {})
  }
  objectPath.set(data, path, payload)
}

const CustomNumber = (props) => {
  let {
    value,
    uischema,
    updateData,
    errors = {},
    label,
    setIsOnChange,
    validateWarning,
    config,
    fieldKey,
    setIsNumberFormatFocus = () => {},
  } = props
  let {
    key,
    elementWidth,
    controlId,
    suffixText,
    precision,
    require,
    tooltipWarning,
    isFieldWarning,
    fieldWarning,
  } = uischema

  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    elementAction,
    isUiChanged,
    setIsUiChanged,
    setFieldFormData,
    setFieldSearchData,
  } = designContext
  const {
    isDynamic = false,
    dynamicKey = '',
    dynamicName = '',
    dynamicIndex = 0,
  } = config

  let errorArray = objectPath.get(errors, key) || []
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire, validateWarning }),
    [uischema, showRequire, validateWarning],
  )

  const warningStyle = useMemo(
    () => createTextWarningStyle({ validateWarning }),
    [validateWarning],
  )
  const [localValue, setLocalValue] = useState(() =>
    getNumberInputValue(value, uischema?.defaultValue),
  )
  const debounceInputName = `number-update-${fieldKey || key}`
  const getNumberValue = (value) => {
    let stringValue = value
    let replaceString = stringValue.replace(',', '')
    let numberValue = replaceString ? Number(replaceString) : replaceString

    return truncateDecimal(numberValue, precision)
  }
  const updateNumberValue = (value) => {
    updateData(getNumberValue(value), true)
  }

  useEffect(() => {
    setLocalValue(getNumberInputValue(value, uischema?.defaultValue))
  }, [value, uischema?.defaultValue])

  useEffect(() => {
    if (isFieldWarning) {
      let formData = jsonForm.formData || {}
      let { label, labelColor = WARNING_COLOR } = validateWarning || {}
      let { key, valueKey } = fieldWarning
      objectPath.set(isUiChanged, `isChangeColor.${key}`, { labelColor })
      setIsUiChanged(isUiChanged)
      if (isDynamic) {
        setFieldFormData(`${dynamicKey}.${dynamicIndex}.${valueKey}`, label)
        setFieldFormData(`${dynamicName}.${dynamicIndex}.${valueKey}`, label)
      } else {
        if (jsonForm.isPage === 1) {
          setFieldSearchData(uischema, key, label)
          setFieldSearchData(uischema, valueKey, label)
          setFieldFormData(key, label)
          setFieldFormData(valueKey, label)
        } else {
          setFieldFormData(key, label)
          setFieldFormData(valueKey, label)
        }
        setObjectPathValue(formData, key, label)
        setObjectPathValue(formData, valueKey, label)
        formData = onCheckCondition(designContext, formData, key, {
          isDebounce: false,
        })
      }
    }
  }, [validateWarning])

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col sm={elementWidth} className="mt-1 p-0">
          <InputGroup style={{ flexWrap: 'unset' }}>
            <InputPrepend isReadOnly={isReadOnly} uischema={uischema} />
            <NumberFormat
              className="form-control"
              style={uiStyle}
              name="value"
              thousandSeparator={uischema?.thousandSeparator}
              autoComplete="off"
              value={localValue}
              readOnly={isReadOnly}
              onChange={(e) => {
                let stringValue = e.target.value
                setLocalValue(stringValue)
                debounceCallback(
                  updateNumberValue,
                  [stringValue],
                  debounceInputName,
                  250,
                )
                setIsOnChange(true)
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  cancelDebounceCallback(debounceInputName)
                  updateNumberValue(ev.target.value)
                  handleEnterAction(ev, uischema, designContext, config)
                  ev.preventDefault()
                  ev.stopPropagation()
                }
              }}
              onFocus={() => setIsNumberFormatFocus(true)}
              onBlur={(e) => {
                cancelDebounceCallback(debounceInputName)
                updateNumberValue(e.target.value)
                setIsNumberFormatFocus(false)
              }}
            />
            <InputAppend isReadOnly={isReadOnly} uischema={uischema} />
          </InputGroup>
        </Col>
        {suffixText && (
          <Col className="d-flex align-items-center mt-1 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`}>{suffixText}</Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}

        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {!isFieldWarning && !tooltipWarning && validateWarning && (
          <li style={warningStyle}>{validateWarning?.label}</li>
        )}

        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

const Overlay = (props) => {
  let { uischema, value } = props
  let { precision, tooltipWarning } = uischema
  const [hover, setHover] = useState(false)
  const [isNumberFormatFocus, setIsNumberFormatFocus] = useState(false)

  const validateWarning = useMemo(() => {
    if (!value) return
    let numberValue = truncateDecimal(value, precision)
    let {
      minLabel = 'ต่ำกว่าปกติ',
      maxLabel = 'สูงกว่าปกติ',
      minLabelColor = WARNING_COLOR,
      maxLabelColor = WARNING_COLOR,
      warning = [],
    } = uischema
    let maxValue = Number(uischema.maxValue)
    let minValue = Number(uischema.minValue)

    if (numberValue > maxValue)
      return { label: maxLabel, labelColor: maxLabelColor }
    if (numberValue < minValue)
      return { label: minLabel, labelColor: minLabelColor }
    let found = warning.find(({ fromValue, toValue }) => {
      return numberValue >= fromValue && numberValue <= toValue
    })
    return found
  }, [value])

  const isInvalid = hover ? tooltipWarning && validateWarning : false
  const tooltipInput = objectPath.get(uischema, 'onEnter.tooltipInput')
  const isShowTooltipEnter = isNumberFormatFocus && Boolean(tooltipInput)

  return (
    <Tooltip
      title={
        <span style={{ fontSize: '14px' }}>
          {isShowTooltipEnter && (
            <span style={{ display: 'block' }}>{tooltipInput}</span>
          )}
          {isInvalid && <span>{validateWarning?.label}</span>}
        </span>
      }
      open={isInvalid || isShowTooltipEnter}
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
    >
      <span
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <CustomNumber
          {...props}
          validateWarning={validateWarning}
          setIsNumberFormatFocus={setIsNumberFormatFocus}
        />
      </span>
    </Tooltip>
  )
}

export default Overlay
